import React, { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import {
  ITravelerProfile,
  IProfileArchetype,
  CreateProfileDto,
  UpdateProfileDto
} from '../../types/profile.types';
import ArchetypeSelector from './ArchetypeSelector';
import ThInputText from '../forms/th-input-text';
import ThInputTextArea from '../forms/th-iput-text-area';
import ThInputSelect from '../forms/th-input-select';
import Button from '../UI/button';
import './TravelerProfileForm.css';

interface TravelerProfileFormProps {
  initialProfile?: ITravelerProfile;
  archetypes: IProfileArchetype[];
  onSave: (dto: CreateProfileDto | UpdateProfileDto) => Promise<void>;
  onCancel: () => void;
}

interface FormState {
  archetypeId: string;
  name: string;
  description: string;
  budgetLevel: ITravelerProfile['budgetLevel'];
  pacePreference: ITravelerProfile['pacePreference'];
  travelStyle: ITravelerProfile['travelStyle'];
  accommodationType: string;
  transportPreference: string;
  primaryTags: string[];
  secondaryTags: string[];
  excludedTags: string[];
  dietaryRestrictions: string[];
  accessibilityNeeds: string[];
  languagePreferences: string[];
  visibility: ITravelerProfile['visibility'];
}

const INITIAL_STATE: FormState = {
  archetypeId: '',
  name: '',
  description: '',
  budgetLevel: 'moderate',
  pacePreference: 'moderate',
  travelStyle: 'solo',
  accommodationType: '',
  transportPreference: '',
  primaryTags: [],
  secondaryTags: [],
  excludedTags: [],
  dietaryRestrictions: [],
  accessibilityNeeds: [],
  languagePreferences: [],
  visibility: 'public'
};

function fromProfile(p: ITravelerProfile): FormState {
  return {
    archetypeId: p.archetypeId,
    name: p.name,
    description: p.description ?? '',
    budgetLevel: p.budgetLevel,
    pacePreference: p.pacePreference,
    travelStyle: p.travelStyle,
    accommodationType: p.accommodationType ?? '',
    transportPreference: p.transportPreference ?? '',
    primaryTags: p.primaryTags,
    secondaryTags: p.secondaryTags,
    excludedTags: p.excludedTags,
    dietaryRestrictions: p.dietaryRestrictions ?? [],
    accessibilityNeeds: p.accessibilityNeeds ?? [],
    languagePreferences: p.languagePreferences ?? [],
    visibility: p.visibility
  };
}

interface ChipInputProps {
  label: string;
  chips: string[];
  onChange: (chips: string[]) => void;
  placeholder?: string;
}

const ChipInput: React.FC<ChipInputProps> = ({
  label,
  chips,
  onChange,
  placeholder
}) => {
  const [inputValue, setInputValue] = useState('');

  const addChip = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !chips.includes(trimmed)) {
      onChange([...chips, trimmed]);
    }
    setInputValue('');
  };

  const removeChip = (chip: string) => {
    onChange(chips.filter((c) => c !== chip));
  };

  return (
    <div className='chip-input mb-3'>
      <label className='form-label'>{label}</label>
      <div className='chip-input__chips'>
        {chips.map((chip) => (
          <span
            key={chip}
            className='chip-input__chip'
          >
            {chip}
            <button
              type='button'
              className='chip-input__remove'
              onClick={() => removeChip(chip)}
              aria-label={`Remove ${chip}`}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className='chip-input__row'>
        <input
          type='text'
          className='form-control form-control-sm'
          value={inputValue}
          placeholder={placeholder ?? 'Type and press Enter'}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addChip();
            }
          }}
        />
        <button
          type='button'
          className='btn btn-outline-secondary btn-sm ms-2'
          onClick={addChip}
        >
          Add
        </button>
      </div>
    </div>
  );
};

const SELECT_OPTIONS = {
  budgetLevel: [
    { value: 'budget', label: 'Budget' },
    { value: 'moderate', label: 'Moderate' },
    { value: 'luxury', label: 'Luxury' },
    { value: 'ultra_luxury', label: 'Ultra Luxury' }
  ],
  pacePreference: [
    { value: 'slow', label: 'Slow' },
    { value: 'moderate', label: 'Moderate' },
    { value: 'fast', label: 'Fast' }
  ],
  travelStyle: [
    { value: 'solo', label: 'Solo' },
    { value: 'couple', label: 'Couple' },
    { value: 'family', label: 'Family' },
    { value: 'group', label: 'Group' },
    { value: 'business', label: 'Business' }
  ],
  accommodationType: [
    { value: '', label: 'No preference' },
    { value: 'hostel', label: 'Hostel' },
    { value: 'hotel', label: 'Hotel' },
    { value: 'resort', label: 'Resort' },
    { value: 'apartment', label: 'Apartment' },
    { value: 'boutique', label: 'Boutique' },
    { value: 'any', label: 'Any' }
  ],
  transportPreference: [
    { value: '', label: 'No preference' },
    { value: 'public', label: 'Public Transit' },
    { value: 'rental', label: 'Rental Car' },
    { value: 'rideshare', label: 'Rideshare' },
    { value: 'walking', label: 'Walking' },
    { value: 'mixed', label: 'Mixed' }
  ],
  visibility: [
    { value: 'public', label: 'Public' },
    { value: 'friends', label: 'Friends Only' },
    { value: 'private', label: 'Private' }
  ]
};

const STEP_LABELS = ['Archetype', 'Preferences', 'Tags', 'Visibility'];

const TravelerProfileForm: React.FC<TravelerProfileFormProps> = ({
  initialProfile,
  archetypes,
  onSave,
  onCancel
}) => {
  const [step, setStep] = useState(initialProfile ? 2 : 1);
  const [form, setForm] = useState<FormState>(
    initialProfile ? fromProfile(initialProfile) : INITIAL_STATE
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!initialProfile;
  const totalSteps = 4;

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleArchetypeSelect = (archetypeId: string) => {
    const archetype = archetypes.find((a) => a._id === archetypeId);
    if (!archetype) return;
    setForm((prev) => ({
      ...prev,
      archetypeId,
      budgetLevel:
        (archetype.defaultBudgetLevel as ITravelerProfile['budgetLevel']) ??
        prev.budgetLevel,
      pacePreference:
        (archetype.defaultPacePreference as ITravelerProfile['pacePreference']) ??
        prev.pacePreference,
      travelStyle:
        (archetype.defaultTravelStyle as ITravelerProfile['travelStyle']) ??
        prev.travelStyle,
      primaryTags:
        archetype.defaultPrimaryTags.length > 0
          ? archetype.defaultPrimaryTags
          : prev.primaryTags
    }));
  };

  const canProceed = (): boolean => {
    if (step === 1) return !!form.archetypeId;
    if (step === 2) return !!form.name.trim();
    return true;
  };

  const canFinish = (): boolean => !!form.archetypeId && !!form.name.trim();

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const dto: CreateProfileDto = {
        archetypeId: form.archetypeId,
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        budgetLevel: form.budgetLevel,
        pacePreference: form.pacePreference,
        travelStyle: form.travelStyle,
        accommodationType:
          (form.accommodationType as ITravelerProfile['accommodationType']) ||
          undefined,
        transportPreference:
          (form.transportPreference as ITravelerProfile['transportPreference']) ||
          undefined,
        primaryTags: form.primaryTags,
        secondaryTags: form.secondaryTags,
        excludedTags: form.excludedTags,
        dietaryRestrictions: form.dietaryRestrictions,
        accessibilityNeeds: form.accessibilityNeeds,
        languagePreferences: form.languagePreferences,
        visibility: form.visibility
      };
      await onSave(dto);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save profile');
      setSaving(false);
    }
  };

  return (
    <div className='tpf'>
      <div className='tpf__steps'>
        {STEP_LABELS.map((label, i) => {
          const s = i + 1;
          return (
            <div
              key={s}
              className={`tpf__step${step === s ? ' tpf__step--active' : ''}${s < step ? ' tpf__step--done' : ''}`}
            >
              <span className='tpf__step-num'>{s < step ? '✓' : s}</span>
              <span className='tpf__step-label'>{label}</span>
            </div>
          );
        })}
      </div>

      <div className='tpf__content'>
        {step === 1 && (
          <div>
            <h6 className='tpf__section-title'>
              Choose Your Traveler Archetype
            </h6>
            <p className='text-muted small mb-3'>
              Your archetype sets the foundation for trip and activity
              recommendations.
            </p>
            <ArchetypeSelector
              archetypes={archetypes}
              selectedId={form.archetypeId || null}
              onSelect={(id) => {
                handleArchetypeSelect(id);
                setField('archetypeId', id);
              }}
            />
          </div>
        )}

        {step === 2 && (
          <div>
            <h6 className='tpf__section-title'>Customize Your Preferences</h6>
            <ThInputText
              label='Profile Name'
              value={form.name}
              onChange={(v: string) => setField('name', v)}
              placeholder='e.g. Weekend Explorer'
              required
              mb={16}
            />
            <ThInputTextArea
              label='Description'
              value={form.description}
              onChange={(v: string) => setField('description', v)}
              rowCount={2}
              required={false}
              mb={16}
            />
            <ThInputSelect
              label='Budget Level'
              value={form.budgetLevel}
              options={SELECT_OPTIONS.budgetLevel}
              onChange={(v: string) =>
                setField('budgetLevel', v as ITravelerProfile['budgetLevel'])
              }
              required
              mb={16}
            />
            <ThInputSelect
              label='Travel Pace'
              value={form.pacePreference}
              options={SELECT_OPTIONS.pacePreference}
              onChange={(v: string) =>
                setField(
                  'pacePreference',
                  v as ITravelerProfile['pacePreference']
                )
              }
              required
              mb={16}
            />
            <ThInputSelect
              label='Travel Style'
              value={form.travelStyle}
              options={SELECT_OPTIONS.travelStyle}
              onChange={(v: string) =>
                setField('travelStyle', v as ITravelerProfile['travelStyle'])
              }
              required
              mb={16}
            />
            <ThInputSelect
              label='Accommodation Type'
              value={form.accommodationType}
              options={SELECT_OPTIONS.accommodationType}
              onChange={(v: string) => setField('accommodationType', v)}
              mb={16}
            />
            <ThInputSelect
              label='Transport Preference'
              value={form.transportPreference}
              options={SELECT_OPTIONS.transportPreference}
              onChange={(v: string) => setField('transportPreference', v)}
              mb={0}
            />
          </div>
        )}

        {step === 3 && (
          <div>
            <h6 className='tpf__section-title'>Tags &amp; Restrictions</h6>
            <p className='text-muted small mb-3'>
              Primary tags are used for trip and activity discovery and
              research.
            </p>
            <ChipInput
              label='Primary Tags'
              chips={form.primaryTags}
              onChange={(chips) => setField('primaryTags', chips)}
              placeholder='e.g. hiking, museums'
            />
            <ChipInput
              label='Secondary Tags'
              chips={form.secondaryTags}
              onChange={(chips) => setField('secondaryTags', chips)}
              placeholder='e.g. nightlife, shopping'
            />
            <ChipInput
              label='Excluded Tags (avoid these)'
              chips={form.excludedTags}
              onChange={(chips) => setField('excludedTags', chips)}
              placeholder='e.g. extreme sports'
            />
            <ChipInput
              label='Dietary Restrictions'
              chips={form.dietaryRestrictions}
              onChange={(chips) => setField('dietaryRestrictions', chips)}
              placeholder='e.g. vegan, gluten-free'
            />
            <ChipInput
              label='Accessibility Needs'
              chips={form.accessibilityNeeds}
              onChange={(chips) => setField('accessibilityNeeds', chips)}
              placeholder='e.g. wheelchair accessible'
            />
            <ChipInput
              label='Language Preferences'
              chips={form.languagePreferences}
              onChange={(chips) => setField('languagePreferences', chips)}
              placeholder='e.g. English, Spanish'
            />
          </div>
        )}

        {step === 4 && (
          <div>
            <h6 className='tpf__section-title'>Visibility</h6>
            <p className='text-muted small mb-3'>
              Control who can see this traveler profile.
            </p>
            <ThInputSelect
              label='Profile Visibility'
              value={form.visibility}
              options={SELECT_OPTIONS.visibility}
              onChange={(v: string) =>
                setField('visibility', v as ITravelerProfile['visibility'])
              }
              required
              mb={16}
            />
          </div>
        )}
      </div>

      {error && <p className='text-danger small mt-2'>{error}</p>}

      <div className='tpf__nav'>
        <Button
          variant='outline-secondary'
          onClick={step === 1 ? onCancel : () => setStep(step - 1)}
        >
          {step === 1 ? 'Cancel' : 'Back'}
        </Button>
        <div className='tpf__nav-right'>
          {step < totalSteps ? (
            <>
              <Button
                variant='primary'
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
              >
                Next
              </Button>
              <Button
                variant='outline-primary'
                onClick={handleSave}
                disabled={saving || !canFinish()}
              >
                {saving ? (
                  <Spinner
                    size='sm'
                    animation='border'
                  />
                ) : (
                  'Finish'
                )}
              </Button>
            </>
          ) : (
            <Button
              variant='primary'
              onClick={handleSave}
              disabled={saving || !canProceed()}
            >
              {saving ? (
                <Spinner
                  size='sm'
                  animation='border'
                />
              ) : isEditing ? (
                'Save Changes'
              ) : (
                'Create Profile'
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TravelerProfileForm;
